// src/features/authn/SsoAdminPanel.tsx
// SSO / OIDC identity provider registry — directory + full-record detail view.
// Provider create/update/delete/enable-toggle already live in platform admin
// (pages/platform/PlatformAuthProvidersPage.tsx); this panel is the read-through
// detail surface for GetOAuth2Provider, the one provider RPC with no call site.

import { useState, useEffect } from 'react';
import { observer } from '@react';
import { useAuthn, useI18n } from '@react/hooks';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, Badge, ErrorAlert } from '@react/components';
import type { OAuth2Provider } from '@core/types/authn';

function formatTimestamp(ts: string | undefined): string {
  if (!ts) return '-';
  try {
    return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(ts));
  } catch {
    return ts;
  }
}

export const SsoAdminPanel = observer(() => {
  const authnStore = useAuthn();
  const { t } = useI18n();

  const [providers, setProviders] = useState<OAuth2Provider[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<OAuth2Provider | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<unknown>(null);

  useEffect(() => {
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await authnStore.ListOAuth2Providers();
      setProviders(resp.providers?.items ?? []);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleView = async (id: string) => {
    setSelectedId(id);
    setDetail(null);
    setDetailError(null);
    setDetailLoading(true);
    try {
      const resp = await authnStore.GetOAuth2Provider(id);
      setDetail(resp.provider ?? null);
    } catch (err) {
      setDetailError(err);
    } finally {
      setDetailLoading(false);
    }
  };

  const Field = ({ label, value, mono }: { label: string; value: React.ReactNode; mono?: boolean }) => (
    <div className="py-2.5 sm:grid sm:grid-cols-3 sm:gap-4">
      <dt className="text-sm font-medium text-muted-foreground">{label}</dt>
      <dd className={`mt-0.5 text-sm text-foreground sm:col-span-2 sm:mt-0 ${mono ? 'font-mono break-all' : ''}`}>
        {value || <span className="text-muted-foreground">{t('common.notSet')}</span>}
      </dd>
    </div>
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
      <Card className="lg:col-span-2 h-fit">
        <CardHeader>
          <CardTitle className="text-base">{t('authn.admin.providersTitle')}</CardTitle>
          <CardDescription>{t('authn.admin.providersDesc')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {!!error && <ErrorAlert error={error} dismissible onDismiss={() => setError(null)} />}
          {loading ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => <div key={i} className="h-14 rounded-lg bg-muted animate-pulse" />)}
            </div>
          ) : providers.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">{t('authn.admin.noProviders')}</p>
          ) : (
            providers.map((p) => (
              <button
                key={p.entity?.id}
                type="button"
                onClick={() => p.entity?.id && handleView(p.entity.id)}
                className={`flex w-full items-center justify-between gap-2 rounded-lg border p-3 text-left transition-colors ${
                  selectedId === p.entity?.id ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                }`}
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{p.provider_name}</p>
                  <p className="text-xs text-muted-foreground">{p.provider_type}</p>
                </div>
                <Badge variant={p.enabled ? 'success' : 'secondary'} size="sm">
                  {p.enabled ? t('platform.authProviders.active') : t('platform.authProviders.inactive')}
                </Badge>
              </button>
            ))
          )}
        </CardContent>
      </Card>

      <Card className="lg:col-span-3 h-fit">
        <CardHeader>
          <CardTitle className="text-base">{t('authn.admin.providerDetail')}</CardTitle>
          <CardDescription>{t('authn.admin.providerDetailDesc')}</CardDescription>
        </CardHeader>
        <CardContent>
          {!!detailError && <ErrorAlert error={detailError} dismissible onDismiss={() => setDetailError(null)} />}
          {!selectedId ? (
            <p className="text-sm text-muted-foreground py-8 text-center">{t('authn.admin.selectProvider')}</p>
          ) : detailLoading ? (
            <div className="space-y-3">
              {[1, 2, 3, 4].map((i) => <div key={i} className="h-6 rounded bg-muted animate-pulse" />)}
            </div>
          ) : detail ? (
            <dl className="divide-y divide-border">
              <Field label={t('authn.clientId')} value={detail.client_id} mono />
              <Field label={t('platform.authProviders.authorizationUrl')} value={detail.authorization_url} mono />
              <Field label={t('platform.authProviders.tokenUrl')} value={detail.token_url} mono />
              <Field label={t('platform.authProviders.userinfoUrl')} value={detail.userinfo_url} mono />
              <Field label={t('platform.authProviders.scopes')} value={(detail.scopes?.items ?? []).join(', ')} />
              <Field
                label={t('authn.admin.signingCertificate')}
                value={detail.signing_certificate_pem
                  ? <Badge variant="secondary" size="sm">{t('authn.admin.configured')}</Badge>
                  : <span className="text-muted-foreground">{t('authn.admin.notConfigured')}</span>}
              />
              <Field label={t('platform.authProviders.priority')} value={String(detail.priority)} />
              <Field label={t('common.createdAt')} value={formatTimestamp(detail.created_at as unknown as string)} />
              <Field label={t('common.updatedAt')} value={formatTimestamp(detail.updated_at as unknown as string)} />
            </dl>
          ) : (
            <p className="text-sm text-muted-foreground py-8 text-center">{t('common.notFound')}</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
});

export default SsoAdminPanel;
